/* global google */

import {useContext} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

const safeAtob = b64str => {
  const base64 = b64str.replace(/-/g, '+').replace(/_/g, '/')
  return decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  )
}

const GOOGLE_AUTHORIZATION_SCOPES = 'email profile openid'
const ERROR_TYPE_POPUP_FAILED = 'popup_failed_to_open'
const ERROR_MESSAGE_POPUP_FAILED = 'Popup window failed to open'
const ERROR_TYPE_POPUP_CLOSED = 'popup_closed'
const ERROR_MESSAGE_POPUP_CLOSED = 'Popup window closed'
const ERROR_MESSAGE_GENERIC = 'Generic error'

const AuthenticateWithGoogle = ({onPrompt = () => {}, onAccessToken = () => {}, origin, prompted = false}) => {
  const {domain} = useContext(Context)

  useMount(() => {
    handleGoogleLoaded()
  })

  const {track} = useAnalyticsTrack()
  const config = domain.get('config')

  const GOOGLE_AUTH_API_KEY = config.get('GOOGLE_AUTH_API_KEY')
  const HOST_NAME_DOMAIN = config.get('HOST_NAME_DOMAIN')

  const {
    TRACKING_EVENT_NAMES: {GOOGLE_ONE_TAP_SEEN, GOOGLE_ONE_TAP_STARTED}
  } = config.get('tracking')

  const handleAuthorizationErrors = async error => {
    let errorMsg
    switch (error.type) {
      case ERROR_TYPE_POPUP_FAILED:
        errorMsg = ERROR_MESSAGE_POPUP_FAILED
        break
      case ERROR_TYPE_POPUP_CLOSED:
        errorMsg = ERROR_MESSAGE_POPUP_CLOSED
        break

      default:
        errorMsg = ERROR_MESSAGE_GENERIC
        break
    }

    const pageError = `google auth2 authorize: ${errorMsg}`
    const trackingErrorEvent = await domain.get('get_event_tracking_data_use_case').execute({
      event: 'Signed In Error',
      origin
    })

    track({...trackingErrorEvent, data: {...trackingErrorEvent.data, page_error: pageError}})
  }

  const handleAuthorization = loginHint => {
    // This method shows to the user a Google modal for granularly accepting scopes
    const googleClient = google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_AUTH_API_KEY,
      scope: GOOGLE_AUTHORIZATION_SCOPES,
      hint: loginHint,
      prompt: 'none',
      callback: tokenResponse => {
        if (tokenResponse && tokenResponse.access_token) {
          // onAccessToken callback pass the token to GigyaSocialNotifier component
          onAccessToken(tokenResponse.access_token)
        }
      },
      error_callback: handleAuthorizationErrors
    })
    googleClient.requestAccessToken()
  }

  const handleAuthentication = credentialResponse => {
    const {credential} = credentialResponse
    if (credential) {
      const parsedCredential = credential.split('.')[1]
      const jwt = JSON.parse(safeAtob(parsedCredential))
      const {sub} = jwt
      handleAuthorization(sub)
    }

    track({
      event: GOOGLE_ONE_TAP_STARTED
    })
  }

  const handleGoogleLoaded = () => {
    if (!prompted) {
      google.accounts.id.initialize({
        client_id: GOOGLE_AUTH_API_KEY,
        callback: handleAuthentication,
        state_cookie_domain: HOST_NAME_DOMAIN,
        itp_support: true,
        use_fedcm_for_prompt: false // Until we have a solution to get an oauth2 token from the client side without the need of a popup it cannot be true
      })

      google.accounts.id.prompt(notification => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          onPrompt()
          return
        }
        if (notification.isDisplayed()) {
          track({
            event: GOOGLE_ONE_TAP_SEEN
          })
        }
      })
    }
  }

  return <div id="google-one-tap-wrapper"></div>
}

AuthenticateWithGoogle.displayName = 'AuthenticateWithGoogle'
AuthenticateWithGoogle.propTypes = {
  onPrompt: PropTypes.func.isRequired,
  onAccessToken: PropTypes.func.isRequired,
  origin: PropTypes.string,
  prompted: PropTypes.bool
}

export default AuthenticateWithGoogle
